/* ═══════════════════════════════════════════════════════════════════════
   component-fit-guide.js  (T5.53)
   Tabbed "Size Guide" — used in BOTH the quick-add modal and the PDP.

   - A "Size Guide" trigger ([data-fg-open]) sits in the Size row.
   - On open it lazy-fetches the matching /pages/{fit-detail} page and pulls
     three sections straight out of the STATIC HTML: the size <table>, the
     Example-Images grid (model photos = CSS background-image divs +
     body-type captions), and the product/measuring diagram.
   - Shows them as 3 tabs (Size Chart / Model Photos / How do I measure?),
     layered on top, responsive. Follows the selected Fit and re-loads on change.

   CONTEXT-AWARE: each host (the modal #quick-view-modal, or a PDP
   [data-fg-host] wrapper) renders its OWN overlay (.lkc-fg) + map (.lkc-fg__map)
   for its product. When a trigger is clicked we resolve its host scope and use
   that scope's overlay/map/fit — so the modal and the PDP never cross wires,
   even when both are present on the same page.

   All listeners are DELEGATED + bound once.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  var cache = {};            // page-handle -> { chart, photos, diagram }
  var activeGuide = null;    // the overlay element currently open
  var activeScope = null;    // its host scope

  /* The host scope for a trigger/element: the quick-add modal, or a PDP wrapper. */
  function scopeFor(el) {
    return el.closest('#quick-view-modal') || el.closest('[data-fg-host]') || document;
  }
  function guideIn(scope) { return scope.querySelector('.lkc-fg'); }
  function isOpen() { return !!(activeGuide && activeGuide.classList.contains('is-open')); }

  /* Handle-ise the currently-checked Fit within a scope (e.g. "Oversized Tee" -> "oversized-tee").
     The modal AND the PDP both tag their radios data-option-name="Fit", so one selector covers both. */
  function currentFitHandle(scope) {
    var checked = scope.querySelector('input[data-option-name="Fit"]:checked')
      || scope.querySelector('.lkc-qv__opt-fit input:checked, input[name="option_fit"]:checked');
    if (!checked) return null;
    return String(checked.value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* The selected Fit's display label (e.g. "Oversized Tee") — shown in the modal title so the
     customer knows which fit's sizing they're viewing. */
  function currentFitLabel(scope) {
    var checked = scope.querySelector('input[data-option-name="Fit"]:checked')
      || scope.querySelector('.lkc-qv__opt-fit input:checked, input[name="option_fit"]:checked');
    return checked ? (checked.value || '') : '';
  }

  /* Look up the detail-page handle for a Fit from the scope's server-rendered map. */
  function pageForFit(scope, fitHandle) {
    var map = scope.querySelector('.lkc-fg__map');
    if (!map || !fitHandle) return null;
    var el = map.querySelector('[data-fit="' + fitHandle + '"]');
    return el ? el.getAttribute('data-page') : null;
  }

  /* T5.97 — Embedded chart HTML for a Fit that has NO detail page (baby onesie/
     tee, kids hoodie/jumper/long-sleeve, work jacket), pulled from the
     <template data-inline> in the map. Sized socks have no Fit option, so they
     carry a single [data-fitless] template used whenever there's no current Fit. */
  function inlineForFit(scope, fitHandle) {
    var map = scope.querySelector('.lkc-fg__map');
    if (!map) return null;
    var el = fitHandle ? map.querySelector('template[data-inline][data-fit="' + fitHandle + '"]') : null;
    if (!el) el = map.querySelector('template[data-inline][data-fitless]');
    return el ? el.innerHTML : null;
  }

  function open(trigger) {
    var scope = scopeFor(trigger);
    var g = guideIn(scope);
    if (!g) return;
    activeGuide = g; activeScope = scope;
    g.classList.add('is-open');
    g.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lkc-fg-lock');
    loadCurrent();
  }
  function close() {
    var g = activeGuide; if (!g) return;
    g.classList.remove('is-open');
    g.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lkc-fg-lock');
  }
  function switchTab(name) {
    var g = activeGuide; if (!g) return;
    g.querySelectorAll('.lkc-fg__tab').forEach(function (t) {
      var on = t.getAttribute('data-fg-tab') === name;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    g.querySelectorAll('.lkc-fg__view').forEach(function (p) {
      p.classList.toggle('is-active', p.getAttribute('data-fg-view') === name);
    });
    var body = g.querySelector('.lkc-fg__body'); if (body) body.scrollTop = 0;
  }

  function loadCurrent() {
    var g = activeGuide, scope = activeScope; if (!g || !scope) return;
    var fitEl = g.querySelector('.lkc-fg__fit');
    if (fitEl) { var lbl = currentFitLabel(scope); fitEl.textContent = lbl ? (' — ' + lbl) : ''; }
    var fitHandle = currentFitHandle(scope);
    var handle = pageForFit(scope, fitHandle);
    if (!handle) {
      /* T5.97 — no detail page: render the embedded inline chart if this fit
         (or a fitless product) has one; otherwise the graceful fallback. */
      var inlineHtml = inlineForFit(scope, fitHandle);
      if (inlineHtml) { render(g, parse(inlineHtml)); return; }
      renderFallback(g); return;
    }
    if (cache[handle]) { render(g, cache[handle]); return; }
    g.classList.add('is-loading');
    fetch('/pages/' + handle, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        g.classList.remove('is-loading');
        if (!html) { renderFallback(g); return; }
        var parsed = parse(html);
        cache[handle] = parsed;
        if (isOpen() && activeGuide === g) render(g, parsed);
      })
      .catch(function () { g.classList.remove('is-loading'); renderFallback(g); });
  }

  /* Clone a section, drop the pages' embedded <style>/<script> (we replicate the
     needed rules, scoped), and un-collapse any accordion body so it's visible. */
  function clean(node) {
    if (!node) return null;
    var c = node.cloneNode(true);
    c.querySelectorAll('style, script').forEach(function (s) { s.remove(); });
    [c].concat(Array.prototype.slice.call(c.querySelectorAll('*'))).forEach(function (e) {
      if (e.classList) e.classList.remove('closed', 'accordion', 'accordion-body');
    });
    return c;
  }

  /* Pull the three sections out of the fetched page's static HTML. */
  function parse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var rte = doc.querySelector('.page-content, .rte, [class*="page-content"]') || doc.body;

    // Size chart = the measurement table.
    var table = rte.querySelector('table');

    // Product diagram = the .sizingphoto in the Size Chart section (same flex-container as the
    // table). Falls back to any bg-image whose URL hints "measure". Robust across garment types.
    var diagram = null;
    if (table) {
      var tfc = table.closest('.flex-container');
      if (tfc) diagram = tfc.querySelector('[style*="background-image"]');
    }
    if (!diagram) {
      diagram = Array.prototype.slice.call(rte.querySelectorAll('[style*="background-image"]'))
        .find(function (e) { return /measure/i.test(e.getAttribute('style') || ''); });
    }

    // Model photos = the .flex-container holding the {Fit}_{Size}_{Model} photos + "...wearing a..." captions.
    var photosWrap = null;
    var exBtn = Array.prototype.slice.call(rte.querySelectorAll('button, summary, h2, h3, strong'))
      .find(function (b) { return /example images|model photos/i.test(b.textContent) && b.textContent.trim().length < 40; });
    if (exBtn) {
      var n = exBtn.nextElementSibling;
      if (n && /wearing/i.test(n.textContent || '')) photosWrap = n;
    }
    if (!photosWrap) {
      photosWrap = Array.prototype.slice.call(rte.querySelectorAll('.grid-x, .grid-container, div')).find(function (e) {
        return /wearing a/i.test(e.textContent || '')
          && e.querySelectorAll('[style*="background-image"]').length > 0
          && e.querySelectorAll('*').length < 80;
      });
    }
    var photos = photosWrap ? (photosWrap.querySelector('.flex-container') || photosWrap) : null;

    var cPhotos = clean(photos), cDiagram = clean(diagram);
    return {
      chart: table ? table.outerHTML : '',
      photos: cPhotos ? cPhotos.outerHTML : '',
      diagram: cDiagram ? cDiagram.outerHTML : ''
    };
  }

  function render(g, p) {
    g.classList.remove('is-loading');
    var chartTable = g.querySelector('.lkc-fg__chart-table');
    var photosSlot = g.querySelector('.lkc-fg__photos-slot');

    if (chartTable) chartTable.innerHTML = p.chart || '<p class="lkc-fg__empty">Size chart not available for this fit.</p>';
    if (photosSlot) photosSlot.innerHTML = p.photos || '';
    // Product diagram appears on every tab that has a slot (Size Chart + How do I measure).
    g.querySelectorAll('.lkc-fg__diagram').forEach(function (d) { d.innerHTML = p.diagram || ''; });

    var hasPhotos = !!p.photos;
    var photoTab = g.querySelector('[data-fg-tab="photos"]');
    if (photoTab) photoTab.hidden = !hasPhotos;

    // If the active tab is now hidden, fall back to Size Chart.
    var active = g.querySelector('.lkc-fg__tab.is-active');
    if (!active || active.hidden) switchTab('chart');
  }

  function renderFallback(g) {
    g.classList.remove('is-loading');
    var chartTable = g.querySelector('.lkc-fg__chart-table');
    if (chartTable) chartTable.innerHTML =
      '<p class="lkc-fg__empty">A detailed guide isn’t available for this fit yet — pick a fit above to see its size chart and model photos.</p>';
    var photosSlot = g.querySelector('.lkc-fg__photos-slot'); if (photosSlot) photosSlot.innerHTML = '';
    g.querySelectorAll('.lkc-fg__diagram').forEach(function (d) { d.innerHTML = ''; });
    var photoTab = g.querySelector('[data-fg-tab="photos"]'); if (photoTab) photoTab.hidden = true;
    switchTab('chart');
  }

  /* ── Delegated, bound-once event wiring ───────────────────────────────── */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-fg-open]');
    if (opener) { e.preventDefault(); open(opener); return; }
    if (e.target.closest('[data-fg-close]')) { e.preventDefault(); close(); return; }
    var tab = e.target.closest('[data-fg-tab]');
    if (tab) { e.preventDefault(); switchTab(tab.getAttribute('data-fg-tab')); return; }
    // Fit chosen via label click (LKC may set radios programmatically — no change event).
    // Re-load only if the clicked Fit control is inside the open guide's own scope.
    var fitCtl = e.target.closest('.lkc-qv__opt-fit, .input-wrap.option-fit');
    if (fitCtl && isOpen() && activeScope && activeScope.contains(fitCtl)) {
      setTimeout(function () { if (isOpen()) loadCurrent(); }, 40);
    }
  });
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('input[data-option-name="Fit"], .lkc-qv__opt-fit input, input[name="option_fit"]')) {
      if (isOpen() && activeScope && activeScope.contains(t)) loadCurrent();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) close();
  });
})();
